/**
 * Prints an 80 mm thermal voucher slip for a spend-reward voucher.
 * Uses a hidden iframe so the browser fires the OS print dialog
 * without navigating away from the till.
 *
 * Voucher shape (all fields optional except code, voucher_value, expiry_date):
 *   { code, promotion_name, voucher_value, voucher_description,
 *     expiry_date, issued_at, issued_sale_number, issued_cashier,
 *     issued_customer_name }
 *
 * Store settings shape (optional):
 *   { company_name, company_address, company_contact }
 */
export function printVoucherSlip(voucher, storeSettings = {}) {
  if (!voucher || !voucher.code) return;

  const rand = (v) =>
    "R " + (Number(v) || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  const fmt = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  };

  const fmtDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("en-ZA", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      });
    } catch {
      return iso;
    }
  };

  const storeName = storeSettings.company_name || "BOSVELD BOU & HARDWARE";
  const storeAddr = storeSettings.company_address || "";
  const storeContact = storeSettings.company_contact || "";

  const html = `
<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  <title>Voucher ${voucher.code}</title>
  <style>
    @page { size: 80mm auto; margin: 0; }
    html, body { margin: 0; padding: 0; }
    body {
      width: 80mm;
      font-family: 'Courier New', monospace;
      color: #000;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      padding: 4mm 3mm 8mm;
      /* Bump the base weight so EVERY line prints dark on a fading
         thermal head — the previous slip had faint expiry/issued/fine
         print lines that were unreadable. */
      font-size: 13px;
      font-weight: 700;
      line-height: 1.45;
    }
    .center { text-align: center; }
    .bold { font-weight: 900; }
    .big { font-size: 17px; font-weight: 900; }
    .huge { font-size: 24px; letter-spacing: 2px; font-weight: 900; }
    hr {
      border: 0;
      border-top: 2px dashed #000;
      margin: 6px 0;
    }
    .code-box {
      border: 3px solid #000;
      padding: 8px 4px;
      margin: 8px 0;
      text-align: center;
    }
    .value {
      font-size: 30px;
      font-weight: 900;
      margin: 6px 0;
    }
    .footer {
      font-size: 12px;
      font-weight: 700;
      margin-top: 8px;
    }
    .kv {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      font-weight: 700;
      margin: 2px 0;
    }
  </style>
</head>
<body>
  <div class="center bold big">${storeName}</div>
  ${storeAddr ? `<div class="center bold" style="font-size:12px;">${storeAddr}</div>` : ""}
  ${storeContact ? `<div class="center bold" style="font-size:12px;">${storeContact}</div>` : ""}
  <hr>
  <div class="center bold big">GIFT / REWARD VOUCHER</div>
  <div class="center bold">${voucher.voucher_description || voucher.promotion_name || ""}</div>
  <div class="value center">${rand(voucher.voucher_value)}</div>

  <div class="code-box">
    <div class="bold" style="font-size:12px;">VOUCHER CODE</div>
    <div class="huge">${voucher.code}</div>
  </div>

  <div class="kv"><span>Expires:</span><span class="bold">${fmtDate(voucher.expiry_date)}</span></div>
  <div class="kv"><span>Issued:</span><span>${fmt(voucher.issued_at)}</span></div>
  ${voucher.issued_sale_number ? `<div class="kv"><span>Sale #:</span><span>${voucher.issued_sale_number}</span></div>` : ""}
  ${voucher.issued_cashier ? `<div class="kv"><span>Cashier:</span><span>${voucher.issued_cashier}</span></div>` : ""}
  ${voucher.issued_customer_name ? `<div class="kv"><span>For:</span><span>${voucher.issued_customer_name}</span></div>` : ""}

  <hr>
  <div class="center bold" style="font-size:13px; line-height:1.5;">
    Bring this slip back to the till<br>
    to redeem your reward.<br>
    Single-use only. No change given.
  </div>
  <hr>
  <div class="footer center">
    Not redeemable for cash.<br>
    Voucher will expire on the date shown above.
  </div>
</body>
</html>`;

  // Print via a throwaway iframe so we don't lose focus on the till page.
  const frame = document.createElement("iframe");
  frame.style.position = "fixed";
  frame.style.right = "0";
  frame.style.bottom = "0";
  frame.style.width = "0";
  frame.style.height = "0";
  frame.style.border = "0";
  document.body.appendChild(frame);

  const doc = frame.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();

  const trigger = () => {
    try {
      frame.contentWindow.focus();
      frame.contentWindow.print();
    } catch (e) {
      console.error("Voucher print failed:", e);
    }
    // Give the print dialog time to open/close before we tear down.
    setTimeout(() => {
      if (frame.parentNode) frame.parentNode.removeChild(frame);
    }, 2000);
  };

  // Some browsers need a beat before contentWindow.print() works.
  setTimeout(trigger, 250);
}
